import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth, logException } from "@um/ui";
import { useSupportStore } from "@/store/useSupportStore";
import { submitSupportRequest } from "@/services/feedback";

/**
 * Slide-up drawer for contacting support or sending feedback. Opened via
 * `useSupportStore.openSupport()` from the profile menu; mounted once at the
 * app root so it's available on every route.
 */
export function SupportDrawer() {
  const { accountId } = useAuth();
  const { isOpen, close } = useSupportStore();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const canSubmit = !!accountId && !!message.trim();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
      setMessage("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || sending) return;

    setSending(true);
    try {
      await submitSupportRequest(accountId!, message);
      toast.success(
        "Thanks! Your message has been sent and will be reviewed by our team soon."
      );
      close();
      setMessage("");
    } catch (error) {
      logException(error);
      console.error("[support] Failed to submit support request", error);
      toast.error(
        "Couldn't send your message. Please try again, or email support@uniqueminds.com."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-col overflow-y-auto"
        >
          <DrawerHeader>
            <DrawerTitle>Support &amp; Feedback</DrawerTitle>
            <DrawerDescription>
              Need help, or have feedback to share? Send a message to the Unique Minds team. We
              read every one.
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex flex-col gap-3 px-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help you today?"
              rows={5}
              className="w-full resize-none"
            />
          </div>

          <DrawerFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit || sending}
            >
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {sending ? "Sending…" : "Send message"}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
